import { DialogContentText } from "@material-ui/core";
import ActionDialog from "@saleor/components/ActionDialog";
import DeleteFilterTabDialog from "@saleor/components/DeleteFilterTabDialog";
import SaveFilterTabDialog, {
  SaveFilterTabDialogFormData
} from "@saleor/components/SaveFilterTabDialog";
import { DEFAULT_INITIAL_PAGINATION_DATA } from "@saleor/config";
import useListSettings from "@saleor/hooks/useListSettings";
import useNavigator from "@saleor/hooks/useNavigator";
import usePaginator, {
  createPaginationState
} from "@saleor/hooks/usePaginator";
import useShop from "@saleor/hooks/useShop";
import { getStringOrPlaceholder } from "@saleor/misc";
import { filterApi } from "@saleor/paymentEvents/util";
import { ListViews } from "@saleor/types";
import createDialogActionHandlers from "@saleor/utils/handlers/dialogActionHandlers";
import createFilterHandlers from "@saleor/utils/handlers/filterHandlers";
import React from "react";
import { useIntl } from "react-intl";

import PaymentEventsListPage from "../../components/PaymentEventsListPage";
import { usePaymentEventsQuery } from "../../queries";
import {
  PaymentEventsListUrl,
  PaymentEventsListUrlDialog,
  PaymentEventsListUrlQueryParams
} from "../../url";
import {
  areFiltersApplied,
  deleteFilterTab,
  getActiveFilters,
  getFilterOpts,
  getFilterQueryParam,
  getFilterTabs,
  getFilterVariables,
  saveFilterTab
} from "./filters";
interface PaymentEventsListProps {
  params: PaymentEventsListUrlQueryParams;
}

export const BrandVerticalList: React.FC<PaymentEventsListProps> = ({
  params
}) => {
  const navigate = useNavigator();
  const paginate = usePaginator();
  const shop = useShop();
  const intl = useIntl();
  const currencySymbol = shop?.defaultCurrency || "PHP";

  const { updateListSettings, settings } = useListSettings(
    ListViews.PAYMENT_EVENTS
  );

  const paginationState = createPaginationState(settings.rowNumber, params);

  const previewVariables = React.useMemo(
    () => ({
      ...paginationState,
      filter: getFilterVariables(params)
    }),
    [params]
  );

  React.useEffect(
    () =>
      navigate(
        PaymentEventsListUrl({
          ...params,
          ...DEFAULT_INITIAL_PAGINATION_DATA
        }),
        true
      ),
    [settings.rowNumber]
  );

  const tabs = getFilterTabs();

  const currentTab =
    params.activeTab === undefined
      ? areFiltersApplied(params)
        ? tabs.length + 1
        : 0
      : parseInt(params.activeTab, 0);

  // Todo - <Marco>: Explore on how to fix from BE
  React.useEffect(() => {
    if (params !== undefined) {
      params.after = null;
      params.before = null;
    }

    if (currentTab !== 0) {
      navigate(
        PaymentEventsListUrl({
          ...params
        })
      );
    }
  }, [
    params.query,
    params.paymentMethodType,
    params.orderCode,
    params.paymentMethodType,
    params.reference,
    params.transactionId
  ]);

  const [
    changeFilters,
    resetFilters,
    handleSearchChange
  ] = createFilterHandlers({
    createUrl: PaymentEventsListUrl,
    getFilterQueryParam,
    navigate,
    params
  });

  const [openModal, closeModal] = createDialogActionHandlers<
    PaymentEventsListUrlDialog,
    PaymentEventsListUrlQueryParams
  >(navigate, PaymentEventsListUrl, params);

  const handleTabChange = (tab: number) => {
    navigate(
      PaymentEventsListUrl({
        activeTab: tab.toString(),
        ...getFilterTabs()[tab - 1].data
      })
    );
  };

  const handleTabDelete = () => {
    deleteFilterTab(currentTab);
    navigate(PaymentEventsListUrl());
  };

  const handleTabSave = (data: SaveFilterTabDialogFormData) => {
    saveFilterTab(data.name, getActiveFilters(params));
    handleTabChange(tabs.length + 1);
  };

  const { data: previewData } = usePaymentEventsQuery({
    displayLoader: true,
    skip: Object.keys(params).length === 0,
    variables: previewVariables
  });

  const { loadNextPage, loadPreviousPage, pageInfo } = paginate(
    previewData?.paymentEvents?.pageInfo,
    paginationState,
    params
  );

  const showPaymentEvents = previewData?.paymentEvents?.edges.map(
    edge => edge.node
  );

  const showApi = showPaymentEvents?.filter(e => e.id === params.id);
  const apiParams = params.id ? filterApi(showApi[0]?.parameters) : null;

  return (
    <>
      <PaymentEventsListPage
        paymentEvents={showPaymentEvents}
        currencySymbol={currencySymbol}
        currentTab={currentTab}
        filterOpts={getFilterOpts(params)}
        initialSearch={params.query || ""}
        onSearchChange={handleSearchChange}
        onFilterChange={changeFilters}
        onAll={resetFilters}
        onTabChange={handleTabChange}
        onTabDelete={() => openModal("delete-search")}
        onTabSave={() => openModal("save-search")}
        tabs={tabs.map(tab => tab.name)}
        pageInfo={pageInfo}
        onNextPage={loadNextPage}
        onPreviousPage={loadPreviousPage}
        onUpdateListSettings={updateListSettings}
        settings={settings}
        onView={(api: any, id: any) => openModal("view", { api, id })}
        onRowClick={() => () => null}
        disabled={false}
      />

      <ActionDialog
        open={params.action === "view"}
        onClose={closeModal}
        onConfirm={closeModal}
        confirmButtonState={undefined}
        hideCancelButton
        maxWidth="xl"
        title={intl.formatMessage({
          defaultMessage: "API Data",
          description: "dialog header"
        })}
      >
        <DialogContentText>
          <div>
            <pre>
              {params.api === "request"
                ? JSON.stringify(apiParams?.apiRequest, null, 4)
                : JSON.stringify(apiParams?.apiResponse, null, 4)}
            </pre>
          </div>
        </DialogContentText>
      </ActionDialog>

      <SaveFilterTabDialog
        open={params.action === "save-search"}
        confirmButtonState="default"
        onClose={closeModal}
        onSubmit={handleTabSave}
      />

      <DeleteFilterTabDialog
        open={params.action === "delete-search"}
        confirmButtonState="default"
        onClose={closeModal}
        onSubmit={handleTabDelete}
        tabName={getStringOrPlaceholder(tabs[currentTab - 1]?.name)}
      />
    </>
  );
};

export default BrandVerticalList;
